import React, { useEffect, useRef, useState, useCallback } from "react";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";

const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";
const NOMINATIM_USER_AGENT = "MultiIQPropertyApp/1.0 (address suggestions)";

// Nominatim: free, no API key. Rate limit 1 req/sec – we debounce.
async function fetchNominatimSuggestions(query) {
  if (!query || query.trim().length < 3) return [];
  const res = await fetch(
    `${NOMINATIM_URL}?format=json&addressdetails=1&limit=5&q=${encodeURIComponent(query.trim())}`,
    { headers: { "Accept-Language": "en", "User-Agent": NOMINATIM_USER_AGENT } }
  );
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

function getAddressPartsFromNominatim(addr) {
  if (!addr) return { city: "", province: "", community: "" };
  return {
    city: addr.city || addr.town || addr.village || addr.municipality || addr.county || "",
    province: addr.state || addr.province || "",
    community: addr.neighbourhood || addr.suburb || addr.district || "",
  };
}

const GOOGLE_SCRIPT_ID = "google-maps-places-script";

function loadGoogleScript(apiKey, callback) {
  if (window.google?.maps?.places) {
    callback();
    return;
  }
  const existing = document.getElementById(GOOGLE_SCRIPT_ID);
  if (existing) {
    existing.addEventListener("load", callback);
    return;
  }
  const script = document.createElement("script");
  script.id = GOOGLE_SCRIPT_ID;
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
  script.async = true;
  script.defer = true;
  script.onload = callback;
  document.head.appendChild(script);
}

function getAddressComponentsFromGoogle(place) {
  const components = place.address_components || [];
  let city = "";
  let province = "";
  let community = "";
  let street = "";
  let streetNumber = "";
  for (const c of components) {
    if (c.types.includes("locality")) city = c.long_name;
    if (c.types.includes("administrative_area_level_1")) province = c.short_name;
    if (c.types.includes("sublocality_level_1") && !city) city = c.long_name;
    if (c.types.includes("neighbourhood")) community = c.long_name;
    if (c.types.includes("sublocality") && !community) community = c.long_name;
    if (c.types.includes("route")) street = c.long_name;
    if (c.types.includes("street_number")) streetNumber = c.long_name;
  }
  return {
    address: place.formatted_address || "",
    city,
    province,
    community,
    street: [streetNumber, street].filter(Boolean).join(" "),
  };
}

/**
 * Address input with suggestions.
 * - Free: uses OpenStreetMap Nominatim (no API key, rate limit ~1/sec).
 * - Optional: if REACT_APP_GOOGLE_MAPS_API_KEY is set, uses Google Places instead.
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Start typing an address…",
  className,
  disabled,
  required,
  ...inputProps
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [scriptReady, setScriptReady] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapperRef = useRef(null);
  const googleKey = typeof process !== "undefined" && process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const useGoogle = Boolean(googleKey);

  useEffect(() => {
    if (!googleKey) return;
    loadGoogleScript(googleKey, () => setScriptReady(true));
  }, [googleKey]);

  // Google Places
  useEffect(() => {
    if (!useGoogle || !scriptReady || !inputRef.current || !window.google?.maps?.places) return;
    if (autocompleteRef.current) return;
    const Autocomplete = window.google.maps.places.Autocomplete;
    const autocomplete = new Autocomplete(inputRef.current, {
      types: ["address"],
      fields: ["formatted_address", "address_components"],
    });
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.formatted_address) return;
      const { address, city, province, community } = getAddressComponentsFromGoogle(place);
      onChange?.(address);
      if (onPlaceSelect) onPlaceSelect({ address, city, province, community });
    });
    autocompleteRef.current = autocomplete;
    return () => {
      if (window.google?.maps?.event && autocompleteRef.current)
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    };
  }, [useGoogle, scriptReady, onChange, onPlaceSelect]);

  // Nominatim (free): debounced search
  const fetchSuggestions = useCallback(async (q) => {
    setLoading(true);
    try {
      const list = await fetchNominatimSuggestions(q);
      setSuggestions(list);
      setHighlight(-1);
      setOpen(list.length > 0);
    } finally {
      setLoading(false);
    }
  }, []);

  const debounceRef = useRef(null);
  const onInputChange = useCallback(
    (e) => {
      const v = e.target.value;
      onChange?.(v);
      if (useGoogle) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (v.trim().length < 3) {
        setSuggestions([]);
        setOpen(false);
        return;
      }
      debounceRef.current = setTimeout(() => fetchSuggestions(v), 400);
    },
    [onChange, useGoogle, fetchSuggestions]
  );

  const pickNominatim = useCallback(
    (item) => {
      const address = item.display_name || "";
      const { city, province, community } = getAddressPartsFromNominatim(item.address);
      onChange?.(address);
      if (onPlaceSelect) onPlaceSelect({ address, city, province, community });
      setSuggestions([]);
      setOpen(false);
    },
    [onChange, onPlaceSelect]
  );

  // Only handle navigation keys so Space and other keys work normally in the input
  const handleKeyDown = useCallback(
    (e) => {
      if (!open || suggestions.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => (h < suggestions.length - 1 ? h + 1 : h));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => (h > 0 ? h - 1 : -1));
      } else if (e.key === "Enter" && highlight >= 0 && suggestions[highlight]) {
        e.preventDefault();
        pickNominatim(suggestions[highlight]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        setHighlight(-1);
      }
      // Do not preventDefault for Space or any other key – let the input behave normally
    },
    [open, suggestions, highlight, pickNominatim]
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={cn("space-y-1", className)} ref={wrapperRef}>
      <div className="relative">
        <Input
          ref={inputRef}
          value={value}
          onChange={useGoogle ? (e) => onChange?.(e.target.value) : onInputChange}
          onFocus={() => !useGoogle && suggestions.length > 0 && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className="h-11"
          autoComplete="off"
          {...inputProps}
        />
        {!useGoogle && loading && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">Searching…</span>
        )}
        {!useGoogle && open && suggestions.length > 0 && (
          <ul className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover py-1 shadow-md max-h-48 overflow-auto top-full left-0 right-0">
            {suggestions.map((item, i) => (
              <li key={item.place_id || i}>
                <button
                  type="button"
                  className={cn(
                    "w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground",
                    i === highlight && "bg-accent/20"
                  )}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => pickNominatim(item)}
                >
                  {item.display_name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
